import { Menu, X } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Modal from '../../modals/Modal'

import AuthModal from '@/components/modals/auth-modal'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { AuthContext } from '@/contexts/auth-context'
import { navigateCMSpage, navigatetoHome } from '@/utils/footer-navigations'
import TrialModal from '../../stripe-components/TrialModal'
import logger from '../../../utils/logger'
import { getBusinessSubscriptionService } from '../../../services'

function BusinessHeader() {
  const [modalContent, setModalContent] = useState(null)
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const [hasUsedTrialBefore, setHasUsedTrialBefore] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { isLoginModalOpen, setIsLoginModalOpen, auth, resetCredentials } = useContext(AuthContext)
  const navigate = useNavigate()

  const fetchAllSubscription = async () => {
    try {
      const response = await getBusinessSubscriptionService()

      // The response.data.data is directly the array of subscription objects you provided
      const allSubscriptions = response.data.data

      // Filter subscriptions where trialStart and trialEnd are not null
      const subscriptionsWithTrials = allSubscriptions.filter(
        (subscription) => subscription.trialStart !== null && subscription.trialEnd !== null
      )

      if (subscriptionsWithTrials.length > 0 && subscriptionsWithTrials) {
        setHasUsedTrialBefore(true)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  useEffect(() => {
    fetchAllSubscription()
  }, [])

  const handleLoginButtonClick = () => {
    if (!isLoginModalOpen) {
      setIsLoginModalOpen(true)
    }
    setModalContent(<AuthModal />)
  }

  const handleHomeButtonclick = () => {
    navigate('/')
    setMenuOpen(false) // Close menu on nav
  }

  const handleLogout = () => {
    localStorage.removeItem('accessToken')
    resetCredentials()
    setMenuOpen(false)
  }

  // replace setShowCardInput(true) with this:
  const handleStartTrialClick = () => {
    setIsTrialModalOpen(true)
  }

  // Determine if the "Start trial" button should be shown
  const showStartFreeButton =
    auth?.user?.role === 'Business' &&
    !hasUsedTrialBefore &&
    (!auth?.user?.subscription ||
      auth?.user?.subscription?.status !== 'active' ||
      auth?.user?.subscription?.status === 'triling')
  return (
    <div className="bg-gradient-to-b from-[#F7E5FF] via-[#F7E6FF] via-10% to-[#D6E0FF] to-100% w-full h-20 shadow-md border-b border-[#e0e0e0]">
      <Modal isOpen={isLoginModalOpen}>{modalContent}</Modal>
      <TrialModal open={isTrialModalOpen} onClose={() => setIsTrialModalOpen(false)} />

      <div className="w-full h-full flex justify-between items-center px-4 sm:px-6 lg:px-[320px] py-3 relative">
        {/* Logo */}
        <div className="z-10">
          <img
            src="/Logo.svg"
            alt="Logo"
            className="h-10 md:h-15 md:w-15 lg:w-auto lg:h-auto lg:mb-[-80px] "
          />
        </div>

        {/* Hamburger menu */}
        <div className="sm:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Navigation */}
        <div
          className={`sm:flex sm:justify-between sm:w-auto ${
            menuOpen ? 'block absolute top-20 left-0 right-0 bg-white shadow-lg p-4 z-50' : 'hidden'
          } sm:block sm:static sm:bg-transparent sm:shadow-none sm:p-0`}
        >
          <ul className="flex flex-col sm:flex-row sm:gap-[40px] gap-4 items-center text-base font-medium text-heading sm:ml-auto">
            <li className="cursor-pointer hover:text-button-purple" onClick={handleHomeButtonclick}>
              Home
            </li>
            <li
              onClick={() => navigatetoHome('/#services')}
              className="cursor-pointer hover:text-button-purple"
            >
              Services
            </li>
            <li
              onClick={() => navigateCMSpage('about-us')}
              className="cursor-pointer hover:text-button-purple"
            >
              About Us
            </li>
            <li
              onClick={() => navigatetoHome('/#prices')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              Pricing
            </li>
            <li
              onClick={() => navigatetoHome('/contact-us')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              Contact Us
            </li>

            <li className="w-full sm:w-auto flex justify-center">
              {auth?.user ? (
                <>
                  {showStartFreeButton && (
                    <Button
                      onClick={() => {
                        handleStartTrialClick()
                        setMenuOpen(false)
                      }}
                      className="cursor-pointer rounded-xl bg-[#4c2859] text-white
                                 text-sm px-4 py-2 mr-4 font-semibold shadow-md
                                 hover:bg-[#643d73] hover:scale-105 transform transition-all duration-300
                                 focus:ring-4 focus:ring-purple-300"
                    >
                      ✨ Start Free Trial ✨
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Avatar className="cursor-pointer">
                        <AvatarImage
                          src={
                            auth?.user?.profile_image
                              ? `${import.meta.env.VITE_BASE_URL}${auth?.user?.profile_image}`
                              : '/profile.svg'
                          }
                        />
                        <AvatarFallback>
                          {auth?.user?.name?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        onClick={() => {
                          navigate('/business/profile')
                          setMenuOpen(false)
                        }}
                        className=" cursor-pointer text-blue-500"
                      >
                        Profile
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="text-red-500  cursor-pointer"
                      >
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => {
                    handleLoginButtonClick()
                    setMenuOpen(false)
                  }}
                  className="rounded-xl bg-button-purple text-white cursor-pointer hover:bg-white hover:text-button-purple font-semibold"
                >
                  Login/Register
                </Button>
              )}
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default BusinessHeader
